// KitchenSink — internal preview/QA harness for the reusable library (Phase 1
// exit criteria: every reusable rendered in isolation, all states, responsive).
// Not a product page. Import everything from the barrel only.
import { useState } from 'react';
import {
  PageContainer,
  PageHead,
  SectionCard,
  Surface,
  Grid,
  Stack,
  Tabs,
  Dropdown,
  PeriodFilter,
  CommandPalette,
  Eyebrow,
  StatusPill,
  AsOfPill,
  ExternalCTA,
  Tooltip,
  Money,
  Avatar,
  AlertCapsule,
  ChangeChip,
  KpiCard,
  MetricCard,
  QuickActionButton,
  TipCard,
  SegBar,
  Sparkline,
  ArcGauge,
  DataTable,
  sortRows,
  TableToolbar,
  Pagination,
  PerfNum,
  TextField,
  Select,
  Switch,
  FormRow,
  RateControl,
  Modal,
  ConfirmDialog,
  Drawer,
  BottomSheet,
  Accordion,
  ProcessCard,
  Skeleton,
  EmptyState,
  ErrorState,
  Spinner,
  ProgressBar,
  GenieTrigger,
  useToast,
} from '../../components/reusable';
import { useThemeBridge } from '../../theme/ThemeBridgeProvider';
import { SEARCH_GROUPS } from '../../config/searchCatalog';
import { TrendIcon, DownloadIcon, PlusIcon, RefreshIcon, BellIcon } from '../../components/icons/Icons';
import './kitchen-sink.css';

const FUNDS = [
  { id: 1, fund: 'Vanguard 500 Index', ticker: 'VFIAX', value: 84210.55, ret: 12.4 },
  { id: 2, fund: 'Fidelity Total Bond', ticker: 'FTBFX', value: 32110.0, ret: -2.1 },
  { id: 3, fund: 'T. Rowe Price Blue Chip', ticker: 'TRBCX', value: 51890.32, ret: 8.7 },
];

export default function KitchenSink() {
  const { mode, toggleMode, cardStyle, setCardStyle } = useThemeBridge();
  const { showToast } = useToast();
  const [period, setPeriod] = useState('1Y');
  const [tab, setTab] = useState('fund');
  const [sort, setSort] = useState({ key: 'value', dir: 'desc' });
  const [rate, setRate] = useState(6);
  const [modal, setModal] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [acc, setAcc] = useState(true);
  const [proc, setProc] = useState(true);
  const [text, setText] = useState('');
  const [sel, setSel] = useState('');
  const [sw, setSw] = useState(true);
  const [cmdOpen, setCmdOpen] = useState(false);

  const columns = [
    { key: 'fund', label: 'Fund' },
    { key: 'ticker', label: 'Ticker' },
    { key: 'value', label: 'Value', numeric: true, render: (v) => <Money value={v} /> },
    { key: 'ret', label: 'Return', numeric: true, render: (v) => <PerfNum value={v} /> },
  ];

  return (
    <PageContainer>
      <PageHead
        trail={['Kitchen Sink']}
        action={<GenieTrigger onClick={() => showToast({ message: 'R.I.S.A would open here', tone: 'info' })} />}
      />

      {/* Theme controls */}
      <SectionCard title="Theme bridge" eyebrow="Controls" action={<AsOfPill />}>
        <Stack direction="row" gap={3} wrap>
          <button className="ks-btn" onClick={toggleMode}>Mode: {mode}</button>
          <button className="ks-btn" onClick={() => setCardStyle(cardStyle === 'inset' ? 'elevated' : 'inset')}>
            Card: {cardStyle}
          </button>
          <button className="ks-btn" onClick={() => showToast({ message: 'Saved successfully', tone: 'success' })}>
            Toast: success
          </button>
          <button className="ks-btn" onClick={() => showToast({ message: 'Could not save', tone: 'danger' })}>
            Toast: error
          </button>
        </Stack>
      </SectionCard>

      {/* Data display */}
      <SectionCard title="Data display" eyebrow="Atoms">
        <Stack direction="row" gap={3} wrap align="center">
          <Eyebrow tone="primary">Eyebrow</Eyebrow>
          <StatusPill tone="success">On track</StatusPill>
          <StatusPill tone="warning">Action needed</StatusPill>
          <StatusPill tone="danger">Overdue</StatusPill>
          <AsOfPill />
          <Tooltip content="Helpful hint"><span className="ks-chip">Hover me</span></Tooltip>
          <Money value={284612.4} splitCents size="lg" />
          <Money value={-1820} tone="loss" />
          <ChangeChip tone="gain" dir="up">+$12,840 · +4.8% this year</ChangeChip>
          <ChangeChip tone="loss" dir="down">-2.1% YTD</ChangeChip>
          <Avatar name="Maya Chen" />
          <ExternalCTA onClick={() => {}}>Open statement</ExternalCTA>
          <Dropdown
            trigger={({ toggle, open }) => (
              <button className="ks-btn" onClick={toggle} aria-expanded={open}>Menu ▾</button>
            )}
          >
            {({ close }) => (
              <Stack gap={1}>
                <button className="ks-btn" onClick={close}>Profile</button>
                <button className="ks-btn" onClick={close}>Settings</button>
                <button className="ks-btn" onClick={close}>Sign out</button>
              </Stack>
            )}
          </Dropdown>
        </Stack>
      </SectionCard>

      {/* Global search */}
      <SectionCard title="Command palette" eyebrow="Navigation">
        <Stack direction="row" gap={3} wrap align="center">
          <button className="ks-btn" onClick={() => setCmdOpen(true)}>Open global search</button>
          <span className="ks-chip">or press ⌘K / Ctrl-K in the app</span>
        </Stack>
        <CommandPalette
          open={cmdOpen}
          onClose={() => setCmdOpen(false)}
          onSelect={(item) => {
            setCmdOpen(false);
            showToast({ message: `Selected: ${item.title}`, tone: 'info' });
          }}
          groups={SEARCH_GROUPS}
        />
      </SectionCard>

      {/* Cards */}
      <SectionCard title="Cards" eyebrow="KPI / actions / tips" action={<PeriodFilter options={['1M', '3M', '1Y', 'All']} value={period} onChange={setPeriod} />}>
        <Grid minItemWidth={200} gap={4}>
          <KpiCard label="Account balance" value={<Money value={284612.4} cents={false} />} trend={{ dir: 'up', pct: '8.4%' }} tone="primary" icon={<TrendIcon />} />
          <KpiCard label="YTD return" value="+12.4%" trend={{ dir: 'up', pct: '2.1%' }} tone="success" />
          <KpiCard label="Vested" value="96%" tone="purple" />
          <MetricCard label="Employer match" value={<Money value={1820} cents={false} />} sub="per year" />
        </Grid>
        <Stack direction="row" gap={3} wrap style={{ marginTop: 'var(--s-4)' }}>
          <QuickActionButton icon={<PlusIcon />} label="Add contribution" sub="One-time" />
          <QuickActionButton icon={<RefreshIcon />} label="Rebalance" tone="success" />
          <QuickActionButton icon={<DownloadIcon />} label="Tax forms" tone="purple" />
        </Stack>
        <div style={{ marginTop: 'var(--s-4)', display: 'grid', gap: 'var(--s-2)' }}>
          <TipCard tone="warning" icon={<BellIcon width={16} height={16} />} title="You’re leaving $1,820/yr on the table" body="Contribute 10% to capture the full match." cta="See the math" onDismiss={() => {}} />
        </div>
        <div style={{ marginTop: 'var(--s-4)' }}>
          <Eyebrow>Alert capsules (compact)</Eyebrow>
          <Grid minItemWidth={240} gap={2} style={{ marginTop: 'var(--s-2)' }}>
            <AlertCapsule tone="danger" label="Action required" headline="Beneficiary missing" onClick={() => {}} onDismiss={() => {}} />
            <AlertCapsule tone="warning" label="A Penny Saved 401(k)" headline="Below the full-match rate" onClick={() => {}} />
            <AlertCapsule tone="info" label="Statement ready" headline="Q2 2026 is available" onDismiss={() => {}} />
            <AlertCapsule tone="success" label="On track" headline="96% vested" />
            <AlertCapsule tone="neutral" dot label="Auto-rebalance scheduled" />
          </Grid>
        </div>
      </SectionCard>

      {/* Charts */}
      <SectionCard title="Charts" eyebrow="Custom SVG">
        <Grid minItemWidth={240} gap={5}>
          <div><ArcGauge pct={68} tone="ahead" /></div>
          <Stack gap={3}>
            <SegBar segments={[{ value: 60, color: 'var(--brand-blue)', tooltip: 'Pre-tax 60%' }, { value: 25, color: 'var(--brand-green)', tooltip: 'Match 25%' }, { value: 15, color: 'var(--brand-yellow)', tooltip: 'Roth 15%' }]} interactive height={12} />
            <ProgressBar value={72} tone="primary" showLabel />
            <Sparkline data={[3, 5, 4, 6, 7, 6, 9, 11]} fill width={220} height={48} />
          </Stack>
        </Grid>
      </SectionCard>

      {/* Table */}
      <SectionCard title="Table" eyebrow="List screen-type">
        <TableToolbar search="" onSearch={() => {}} resultCount={FUNDS.length} activeFilters={1} />
        <DataTable
          columns={columns}
          rows={sortRows(FUNDS, sort)}
          sort={sort}
          onSort={setSort}
          rowClassName={(r) => (r.ret >= 0 ? 'ks-row-gain' : '')}
        />
        <Pagination page={1} pageCount={3} onPage={() => {}} pageSize={10} onPageSize={() => {}} />
        <div style={{ marginTop: 'var(--s-4)' }}>
          <Tabs tabs={[{ id: 'fund', label: 'By Fund' }, { id: 'asset', label: 'By Asset Class' }, { id: 'source', label: 'By Source' }]} value={tab} onChange={setTab} />
        </div>
      </SectionCard>

      {/* Forms */}
      <SectionCard title="Forms" eyebrow="Inputs">
        <Grid minItemWidth={240} gap={4}>
          <TextField label="Full name" value={text} onChange={(e) => setText(e.target.value)} required />
          <TextField label="Email" value="" onChange={() => {}} error="Enter a valid email" />
          <Select label="Frequency" value={sel} onChange={(e) => setSel(e.target.value)} options={[{ value: 'wk', label: 'Weekly' }, { value: 'mo', label: 'Monthly' }]} placeholder="Choose…" />
          <FormRow label="Notifications" helperText="Optional"><Switch label="Email me updates" checked={sw} onChange={(e) => setSw(e.target.checked)} /></FormRow>
        </Grid>
        <div style={{ marginTop: 'var(--s-4)', maxWidth: 360 }}>
          <RateControl label="Pre-tax contribution" value={rate} onChange={setRate} max={30} hint="Employer matches up to 6%" sub={`${rate}% of each paycheck`} />
        </div>
      </SectionCard>

      {/* Disclosure */}
      <SectionCard title="Disclosure" eyebrow="Accordion / ProcessCard">
        <Stack gap={3}>
          <Accordion title="What is vesting?" summary="Tap to learn" open={acc} onToggle={() => setAcc((v) => !v)}>
            <p style={{ font: 'var(--body-sm)', color: 'var(--text-2)' }}>Vesting is the portion of employer contributions you own outright.</p>
          </Accordion>
          <ProcessCard tone="green" icon={<TrendIcon />} title="Contributions" question="How much should I save?" ctaLabel="Manage" ctaTone="green" summary="6% pre-tax" open={proc} onToggle={() => setProc((v) => !v)}>
            <p style={{ font: 'var(--body-sm)', color: 'var(--text-2)' }}>Adjust your contribution rate and see the projected impact.</p>
          </ProcessCard>
        </Stack>
      </SectionCard>

      {/* Overlays */}
      <SectionCard title="Overlays" eyebrow="Dialogs / drawers">
        <Stack direction="row" gap={3} wrap>
          <button className="ks-btn" onClick={() => setModal(true)}>Open Modal</button>
          <button className="ks-btn" onClick={() => setConfirm(true)}>Confirm dialog</button>
          <button className="ks-btn" onClick={() => setDrawer(true)}>Open Drawer (R.I.S.A)</button>
          <button className="ks-btn" onClick={() => setSheet(true)}>Bottom sheet</button>
        </Stack>
      </SectionCard>

      {/* Feedback states */}
      <SectionCard title="Feedback states" eyebrow="Loading / empty / error">
        <Grid minItemWidth={220} gap={4}>
          <Surface variant="inset"><Skeleton variant="title" /><Skeleton variant="text" count={3} /></Surface>
          <Surface variant="inset"><EmptyState message="No transactions yet." action={<ExternalCTA>Add one</ExternalCTA>} /></Surface>
          <Surface variant="inset"><ErrorState onRetry={() => showToast({ message: 'Retrying…' })} /></Surface>
          <Surface variant="inset"><Stack align="center" gap={3}><Spinner size="lg" /><span style={{ font: 'var(--caption)', color: 'var(--text-3)' }}>Loading…</span></Stack></Surface>
        </Grid>
      </SectionCard>

      <Modal open={modal} onClose={() => setModal(false)} title="Fund detail" actions={<><button className="ks-btn" onClick={() => setModal(false)}>Close</button><ExternalCTA>View prospectus</ExternalCTA></>}>
        <p style={{ font: 'var(--body)', color: 'var(--text-2)' }}>Vanguard 500 Index Fund (VFIAX) — large-blend, expense ratio 0.04%.</p>
      </Modal>

      <ConfirmDialog
        open={confirm}
        danger
        title="Cancel transfer?"
        message="This will stop the in-progress rollover."
        entity="Rollover · $42,000 from Acme 401(k)"
        consequence="This cannot be undone once submitted."
        confirmLabel="Cancel transfer"
        onConfirm={() => { setConfirm(false); showToast({ message: 'Transfer cancelled', tone: 'success' }); }}
        onCancel={() => setConfirm(false)}
      />

      <Drawer open={drawer} onClose={() => setDrawer(false)} title="Ask Risa">
        <p style={{ font: 'var(--body)', color: 'var(--text-2)' }}>This is where the R.I.S.A assistant panel mounts.</p>
      </Drawer>

      <BottomSheet open={sheet} onClose={() => setSheet(false)} title="Menu">
        <Stack gap={2}>
          <QuickActionButton icon={<PlusIcon />} label="Add contribution" />
          <QuickActionButton icon={<DownloadIcon />} label="Download statement" />
        </Stack>
      </BottomSheet>
    </PageContainer>
  );
}
